"use client";

import Link from "next/link";
import {
  ArrowRight,
  Braces,
  CreditCard,
  Database,
  FileText,
  Landmark,
  Menu,
  ReceiptText,
  Sheet,
  ShoppingCart,
} from "lucide-react";
import { useSession } from "@/lib/auth-client";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@ocr-monorepo/ui/components/dropdown-menu";
import { Button } from "@ocr-monorepo/ui/components/button";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@ocr-monorepo/ui/components/navigation-menu";

const navLinks = [
  { label: "Product", href: "/#product" },
  { label: "MCP", href: "/mcp" },
  { label: "Articles", href: "/blog" },
  { label: "Pricing", href: "/pricing" },
  { label: "Contact", href: "/contact" },
];

const dataFormats = [
  {
    label: "PDF to SQLite",
    description: "Connected tables, ready to query.",
    href: "/pdf-to-sqlite",
    icon: Database,
  },
  {
    label: "PDF to JSON",
    description: "Structured records for your API.",
    href: "/pdf-to-json",
    icon: Braces,
  },
  {
    label: "PDF to CSV",
    description: "Clean rows for your spreadsheet.",
    href: "/pdf-to-csv",
    icon: Sheet,
  },
];

const recordTypes = [
  {
    label: "Invoices",
    description: "Vendors, totals, and line items.",
    href: "/invoice-parser",
    icon: FileText,
  },
  {
    label: "Bank statements",
    description: "Accounts, balances, and transactions.",
    href: "/bank-statement-parser",
    icon: Landmark,
  },
  {
    label: "Credit card statements",
    description: "Balances, due dates, and transactions.",
    href: "/credit-card-statement-parser",
    icon: CreditCard,
  },
  {
    label: "Purchase orders",
    description: "Suppliers, orders, and purchased items.",
    href: "/purchase-order-parser",
    icon: ShoppingCart,
  },
  {
    label: "Receipts",
    description: "Merchants, taxes, and purchases.",
    href: "/receipt-parser",
    icon: ReceiptText,
  },
];

const menuGroups = [
  { label: "Data formats", href: "/data-formats", items: dataFormats },
  { label: "Record types", href: "/record-types", items: recordTypes },
];

function DesktopMenu({ group }: { group: (typeof menuGroups)[number] }) {
  return (
    <NavigationMenuItem>
      <NavigationMenuTrigger className="h-9 rounded-sm bg-transparent px-2 font-normal data-[state=open]:bg-accent data-[state=open]:text-accent-foreground [&_svg]:ml-0">
        {group.label}
      </NavigationMenuTrigger>
      <NavigationMenuContent className="z-[110] border border-border p-2 md:left-[-9px] md:w-[380px] group-data-[viewport=false]/navigation-menu:mt-[9px] group-data-[viewport=false]/navigation-menu:rounded-md group-data-[viewport=false]/navigation-menu:shadow-[0_12px_28px_#20292014] group-data-[viewport=false]/navigation-menu:ring-0">
        <ul className="flex flex-col">
          {group.items.map(({ label, description, href, icon: Icon }) => (
            <li key={href}>
              <NavigationMenuLink
                asChild
                className="min-h-[72px] gap-3 rounded-sm p-3 hover:bg-accent focus:bg-accent focus-visible:bg-accent focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
              >
                <Link href={href}>
                  <span className="flex size-9 shrink-0 items-center justify-center rounded-sm bg-accent text-primary">
                    <Icon
                      className="size-[18px]"
                      strokeWidth={1.6}
                      aria-hidden="true"
                    />
                  </span>
                  <span className="flex min-w-0 flex-col gap-1">
                    <span className="text-sm font-medium leading-5">
                      {label}
                    </span>
                    <span className="text-[13px] leading-5 text-muted-foreground">
                      {description}
                    </span>
                  </span>
                </Link>
              </NavigationMenuLink>
            </li>
          ))}
        </ul>
        <div className="border-t">
          <NavigationMenuLink
            asChild
            className="justify-between rounded-sm p-3 text-[13px] font-medium leading-5 text-primary focus-visible:bg-accent focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
          >
            <Link href={group.href}>
              View all {group.label.toLowerCase()}
              <ArrowRight className="size-4 shrink-0" aria-hidden="true" />
            </Link>
          </NavigationMenuLink>
        </div>
      </NavigationMenuContent>
    </NavigationMenuItem>
  );
}

export function MossHeader() {
  const { data: session } = useSession();
  const isSignedIn = Boolean(session?.user && !session.user.isAnonymous);

  return (
    <header className="isolate sticky top-0 z-[100] border-b bg-background text-foreground">
      <div className="mx-auto flex min-h-20 max-w-[1440px] items-center justify-between gap-6 px-5 py-5 md:px-10 lg:min-h-[97px] lg:px-8 lg:py-[26px] xl:px-20">
        <Link
          href="/"
          className="text-[25px] font-bold leading-[30px] tracking-[-0.05em] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          pdfparse
        </Link>

        <NavigationMenu
          aria-label="Main navigation"
          viewport={false}
          className="z-[110] hidden flex-none lg:flex"
        >
          <NavigationMenuList className="gap-5">
            <NavigationMenuItem>
              <NavigationMenuLink
                asChild
                className="h-auto rounded-none p-0 font-normal leading-[18px] hover:bg-transparent focus:bg-transparent"
              >
                <Link href="/#product">Product</Link>
              </NavigationMenuLink>
            </NavigationMenuItem>
            {menuGroups.map((group) => (
              <DesktopMenu key={group.href} group={group} />
            ))}
            {navLinks.slice(1).map((link) => (
              <NavigationMenuItem key={link.href}>
                <NavigationMenuLink
                  asChild
                  className="h-auto rounded-none p-0 font-normal leading-[18px] hover:bg-transparent focus:bg-transparent"
                >
                  <Link href={link.href}>{link.label}</Link>
                </NavigationMenuLink>
              </NavigationMenuItem>
            ))}
          </NavigationMenuList>
        </NavigationMenu>

        <div className="flex shrink-0 items-center gap-3 xl:gap-7">
          <Link
            href={isSignedIn ? "/dashboard" : "/login"}
            className="hidden text-sm leading-[18px] transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:block"
          >
            {isSignedIn ? "Dashboard" : "Log in"}
          </Link>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="icon-marketing"
                aria-label="Open navigation"
                className="lg:hidden"
              >
                <Menu className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              sideOffset={12}
              collisionPadding={16}
              className="z-[120] w-80 max-w-[calc(100vw-32px)] rounded-md border border-border bg-popover p-2 text-popover-foreground shadow-lg ring-0 [&_[data-slot=dropdown-menu-item]]:min-h-10 [&_[data-slot=dropdown-menu-item]]:gap-3 [&_[data-slot=dropdown-menu-item]]:rounded-sm [&_[data-slot=dropdown-menu-item]]:px-3 [&_[data-slot=dropdown-menu-label]]:px-3 [&_[data-slot=dropdown-menu-label]]:text-xs [&_[data-slot=dropdown-menu-label]]:text-muted-foreground"
            >
              {navLinks.slice(0, 1).map((link) => (
                <DropdownMenuItem key={link.href} asChild>
                  <Link href={link.href}>{link.label}</Link>
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              {menuGroups.map((group) => (
                <div key={group.href}>
                  <DropdownMenuLabel>{group.label}</DropdownMenuLabel>
                  {group.items.map(({ href, label, icon: Icon }) => (
                    <DropdownMenuItem key={href} asChild>
                      <Link href={href}>
                        <Icon
                          className="size-4 text-primary"
                          aria-hidden="true"
                        />
                        {label}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuItem asChild>
                    <Link
                      href={group.href}
                      className="justify-between text-primary"
                    >
                      View all {group.label.toLowerCase()}
                      <ArrowRight className="size-4" aria-hidden="true" />
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </div>
              ))}
              {navLinks.slice(1).map((link) => (
                <DropdownMenuItem key={link.href} asChild>
                  <Link href={link.href}>{link.label}</Link>
                </DropdownMenuItem>
              ))}
              <DropdownMenuItem asChild className="sm:hidden">
                <Link href={isSignedIn ? "/dashboard" : "/login"}>
                  {isSignedIn ? "Dashboard" : "Log in"}
                </Link>
              </DropdownMenuItem>
              {!isSignedIn && (
                <DropdownMenuItem asChild className="sm:hidden">
                  <Link
                    href="/getting-started/create-account?source=header"
                    className="font-medium text-primary"
                  >
                    Start extracting{" "}
                    <ArrowRight className="ml-auto size-4" aria-hidden="true" />
                  </Link>
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          {!isSignedIn && (
            <Button
              asChild
              variant="outline"
              size="marketing-sm"
              className="hidden sm:inline-flex"
            >
              <Link href="/getting-started/create-account?source=header">
                Start extracting ↗
              </Link>
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}
